import type { ComponentProps, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const format = new Intl.NumberFormat('en-US');

/**
 * The bar that floats over a fundraising list while people are selected — the book's and a call book's.
 * It's dark on the white table so it reads as its own layer, and it rises into view when the first person
 * is ticked (the eye is on the checkbox, far from the bar). Each new count gives the label a small bump.
 * Clearing takes it away at once. Motion is off under reduced motion (see index.css).
 */
export function SelectionBar({ count, detail, children }: { count: number; detail?: string; children?: ReactNode }) {
  if (count === 0) return null;
  return (
    <div
      role="toolbar"
      aria-label="Selected people"
      className="animate-selection-bar-in sticky bottom-4 z-10 mx-auto flex h-14 w-fit max-w-full items-center gap-2 rounded-xl bg-foreground pl-5 pr-2 text-background shadow-xl"
    >
      <span
        key={count}
        className="animate-selection-count mr-1 inline-block origin-left whitespace-nowrap text-[13px] font-semibold tabular-nums"
      >
        {format.format(count)} selected{detail && ` · ${detail}`}
      </span>
      {children}
    </div>
  );
}

/** A divider between the bar's groups of actions. */
export function SelectionBarDivider() {
  return <span aria-hidden="true" className="mx-1 h-6 w-px bg-background/20" />;
}

/**
 * An action on the bar: `primary` is the one thing the selection is for (solid fundraising blue); the rest
 * are quiet text on the dark bar. `shortcut` shows the key that does the same thing.
 */
export function SelectionBarButton({
  primary = false,
  shortcut,
  className,
  children,
  ...props
}: Omit<ComponentProps<typeof Button>, 'variant'> & { primary?: boolean; shortcut?: string }) {
  return (
    <Button
      variant={primary ? 'default' : 'ghost'}
      className={cn('h-10', !primary && 'text-background hover:bg-background/15 hover:text-background', className)}
      {...props}
    >
      {children}
      {shortcut && (
        <kbd aria-hidden="true" className="ml-2 font-sans text-xs font-medium opacity-60">
          {shortcut}
        </kbd>
      )}
    </Button>
  );
}
